import React, { useState, useEffect, useRef } from 'react';
import { fetchUserAttributes, updateUserAttributes, getCurrentUser } from 'aws-amplify/auth';

function Profile({ profilePhoto, setProfilePhoto }) {
  const [attrs, setAttrs] = useState(null);
  const [cognitoUsername, setCognitoUsername] = useState('');
  const [laden, setLaden] = useState(true);
  const [bewerken, setBewerken] = useState(false);
  const [voornaam, setVoornaam] = useState('');
  const [achternaam, setAchternaam] = useState('');
  const [bedrijf, setBedrijf] = useState('');
  const [functie, setFunctie] = useState('');
  const [opslaan, setOpslaan] = useState(false);
  const [opgeslagen, setOpgeslagen] = useState(false);
  const [fout, setFout] = useState('');
  const [reminder, setReminder] = useState(false);
  const [reminderOpslaan, setReminderOpslaan] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    Promise.all([fetchUserAttributes(), getCurrentUser()])
      .then(([a, u]) => {
        setAttrs(a);
        setCognitoUsername(u.username);
        setVoornaam(a.given_name || '');
        setAchternaam(a.family_name || '');
        setBedrijf(a['custom:bedrijf'] || '');
        setFunctie(a['custom:functie'] || '');
        setReminder(a['custom:monthly_reminder'] === 'true');
      })
      .catch(() => {})
      .finally(() => setLaden(false));
  }, []);

  const gewijzigd = attrs
    ? voornaam !== (attrs.given_name || '')
      || achternaam !== (attrs.family_name || '')
      || bedrijf !== (attrs['custom:bedrijf'] || '')
      || functie !== (attrs['custom:functie'] || '')
    : false;

  const handleBewerken = () => {
    setVoornaam(attrs?.given_name || '');
    setAchternaam(attrs?.family_name || '');
    setBedrijf(attrs?.['custom:bedrijf'] || '');
    setFunctie(attrs?.['custom:functie'] || '');
    setFout('');
    setBewerken(true);
  };

  const handleAnnuleren = () => {
    setVoornaam(attrs?.given_name || '');
    setAchternaam(attrs?.family_name || '');
    setBedrijf(attrs?.['custom:bedrijf'] || '');
    setFunctie(attrs?.['custom:functie'] || '');
    setFout('');
    setBewerken(false);
  };

  const handleOpslaan = async (e) => {
    e.preventDefault();
    if (!gewijzigd) return;
    setOpslaan(true);
    setFout('');
    try {
      await updateUserAttributes({
        userAttributes: {
          given_name: voornaam,
          family_name: achternaam,
          'custom:bedrijf': bedrijf,
          'custom:functie': functie,
        }
      });
      setAttrs(prev => ({ ...prev, given_name: voornaam, family_name: achternaam, 'custom:bedrijf': bedrijf, 'custom:functie': functie }));
      setBewerken(false);
      setOpgeslagen(true);
      setTimeout(() => setOpgeslagen(false), 2500);
    } catch (err) {
      setFout('Opslaan mislukt: ' + err.message);
    } finally {
      setOpslaan(false);
    }
  };

  const handleReminderToggle = async () => {
    const nieuweWaarde = !reminder;
    setReminder(nieuweWaarde);
    setReminderOpslaan(true);
    try {
      await updateUserAttributes({
        userAttributes: { 'custom:monthly_reminder': nieuweWaarde ? 'true' : 'false' }
      });
    } catch {
      setReminder(!nieuweWaarde);
    } finally {
      setReminderOpslaan(false);
    }
  };

  const handleFotoWijzigen = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxSize = 256;
        let w = img.width, h = img.height;
        if (w > h) { h = Math.round(h * maxSize / w); w = maxSize; }
        else { w = Math.round(w * maxSize / h); h = maxSize; }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        setProfilePhoto(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const initiaal = attrs?.given_name?.[0] || cognitoUsername?.[0] || attrs?.email?.[0] || '?';

  const ReadOnlyField = ({ label, value }) => (
    <div className="bg-gray-50 rounded-lg px-4 py-3">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-sm text-gray-900 font-medium">{value || '—'}</p>
    </div>
  );

  const EditField = ({ label, value, onChange, autoFocus }) => (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
        autoFocus={autoFocus}
      />
    </div>
  );

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-gray-900">Mijn profiel</h2>
          {!bewerken && !laden && (
            <button
              onClick={handleBewerken}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
            >
              Profiel bewerken
            </button>
          )}
        </div>

        {laden ? (
          <p className="text-gray-500 text-center py-8">Laden...</p>
        ) : (
          <div className="flex flex-col sm:flex-row items-start sm:space-x-8 space-y-6 sm:space-y-0">
            <div className="flex flex-col items-center space-y-3 flex-shrink-0">
              <div className="w-28 h-28 rounded-full overflow-hidden bg-blue-100 flex items-center justify-center ring-4 ring-blue-50">
                {profilePhoto
                  ? <img src={profilePhoto} alt="Profielfoto" className="w-full h-full object-cover" />
                  : <span className="text-4xl font-bold text-blue-700">{initiaal.toUpperCase()}</span>
                }
              </div>
              <button onClick={() => fileInputRef.current.click()} className="text-sm text-blue-600 hover:underline font-medium">
                {profilePhoto ? 'Foto wijzigen' : 'Foto uploaden'}
              </button>
              {profilePhoto && (
                <button onClick={() => setProfilePhoto(null)} className="text-sm text-red-500 hover:underline">
                  Foto verwijderen
                </button>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFotoWijzigen} />
            </div>

            <form onSubmit={handleOpslaan} className="flex-1 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ReadOnlyField label="Gebruikersnaam" value={cognitoUsername} />
                <ReadOnlyField label="E-mailadres" value={attrs?.email} />

                {bewerken ? (
                  <>
                    <EditField label="Voornaam" value={voornaam} onChange={setVoornaam} autoFocus />
                    <EditField label="Achternaam" value={achternaam} onChange={setAchternaam} />
                    <EditField label="Bedrijf" value={bedrijf} onChange={setBedrijf} />
                    <EditField label="Functie" value={functie} onChange={setFunctie} />
                  </>
                ) : (
                  <>
                    <ReadOnlyField label="Voornaam" value={attrs?.given_name} />
                    <ReadOnlyField label="Achternaam" value={attrs?.family_name} />
                    <ReadOnlyField label="Bedrijf" value={attrs?.['custom:bedrijf']} />
                    <ReadOnlyField label="Functie" value={attrs?.['custom:functie']} />
                  </>
                )}
              </div>

              {fout && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{fout}</p>}

              {bewerken && (
                <div className="flex items-center space-x-3">
                  <button
                    type="submit"
                    disabled={!gewijzigd || opslaan}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {opslaan ? 'Opslaan...' : 'Opslaan'}
                  </button>
                  <button
                    type="button"
                    onClick={handleAnnuleren}
                    className="text-sm text-gray-600 hover:text-gray-800 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition"
                  >
                    Annuleren
                  </button>
                </div>
              )}

              {opgeslagen && (
                <p className="text-sm text-green-600 font-medium">Wijzigingen opgeslagen</p>
              )}
            </form>
          </div>
        )}

        {!laden && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Notificaties</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-700">Ik ontvang graag een maandelijkse reminder</p>
                <p className="text-xs text-gray-500 mt-0.5">Elke eerste maandag van de maand rond 10:00u — "Vergeet niet jouw reiskosten te declareren in AFAS"</p>
              </div>
              <button
                onClick={handleReminderToggle}
                disabled={reminderOpslaan}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none ${reminder ? 'bg-blue-600' : 'bg-gray-200'} disabled:opacity-50`}
                role="switch"
                aria-checked={reminder}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${reminder ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;
